/**
 * Edit Admin Modal Component
 * Modal for editing admin accounts - change role or send password reset
 */

import React, { useState } from 'react';
import { apiService } from '../../services/apiService';
import './EditAdminModal.css';

interface EditAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin: {
    id: string;
    email: string;
    name: string;
    role: string;
  } | null;
}

type AdminRole = 'master' | 'finance' | 'support' | 'compliance' | 'content' | 'risk';

const EditAdminModal: React.FC<EditAdminModalProps> = ({ isOpen, onClose, onSuccess, admin }) => {
  const [action, setAction] = useState<'role' | 'password'>('role');
  const [selectedRole, setSelectedRole] = useState<AdminRole>(admin?.role as AdminRole || 'finance');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Update selected role when admin changes
  React.useEffect(() => {
    if (admin) {
      setSelectedRole(admin.role as AdminRole);
      setAction('role');
      setError(null);
      setSuccessMessage(null);
    }
  }, [admin]);

  const handleRoleChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!admin) return;

    setError(null);
    setIsLoading(true);

    try {
      await apiService.updateAdminRole(admin.id, selectedRole);
      setSuccessMessage(`Admin role updated to ${getRoleDisplayName(selectedRole)}`);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin role');
      setIsLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!admin) return;

    setError(null);
    setIsLoading(true);

    try {
      await apiService.requestPasswordReset(admin.email);
      setSuccessMessage(`Password reset email sent to ${admin.email}`);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      setSuccessMessage(null);
      setAction('role');
      onClose();
    }
  };

  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      master: 'Master Admin',
      risk: 'Risk & AML Admin',
      finance: 'Finance Admin',
      support: 'Support Admin',
      compliance: 'Compliance Admin',
      content: 'Content Admin',
    };
    return roleMap[role] || role;
  };

  if (!isOpen || !admin) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Admin: {admin.name}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isLoading}>
            ×
          </button>
        </div>

        <div className="edit-admin-content">
          {successMessage ? (
            <div className="edit-admin-success">
              <div className="success-icon">✅</div>
              <p>{successMessage}</p>
            </div>
          ) : (
            <>
              {/* Action Selection */}
              <div className="action-selection">
                <button
                  type="button"
                  className={`action-option ${action === 'role' ? 'active' : ''}`}
                  onClick={() => setAction('role')}
                  disabled={isLoading}
                >
                  <span className="action-icon">👤</span>
                  <span className="action-label">Change Role</span>
                </button>
                <button
                  type="button"
                  className={`action-option ${action === 'password' ? 'active' : ''}`}
                  onClick={() => setAction('password')}
                  disabled={isLoading}
                >
                  <span className="action-icon">🔑</span>
                  <span className="action-label">Send Password Reset</span>
                </button>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {/* Change Role Form */}
              {action === 'role' && (
                <form onSubmit={handleRoleChange} className="edit-admin-form">
                  <div className="form-group">
                    <label htmlFor="role">Admin Role</label>
                    <select
                      id="role"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as AdminRole)}
                      disabled={isLoading || admin.role === 'master'}
                      required
                    >
                      <option value="finance">Finance Admin</option>
                      <option value="support">Support Admin</option>
                      <option value="compliance">Compliance Admin</option>
                      <option value="risk">Risk & AML Admin</option>
                      <option value="content">Content Admin</option>
                      {admin.role === 'master' && <option value="master">Master Admin</option>}
                    </select>
                    {admin.role === 'master' && (
                      <p className="form-note" style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        Master Admin role cannot be changed
                      </p>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="button-secondary"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="button-primary"
                      disabled={isLoading || admin.role === 'master' || selectedRole === admin.role}
                    >
                      {isLoading ? 'Updating...' : 'Update Role'}
                    </button>
                  </div>
                </form>
              )}

              {/* Send Password Reset */}
              {action === 'password' && (
                <div className="edit-admin-form">
                  <div className="password-reset-info">
                    <p>This will send a password reset email to:</p>
                    <p className="admin-email">{admin.email}</p>
                    <p className="info-note">
                      The admin will receive an email with a password reset link that expires in 1 hour.
                      This is the same flow as the "Forgot Password" feature.
                    </p>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="button-secondary"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="button-primary"
                      onClick={handleSendPasswordReset}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Sending...' : 'Send Password Reset Email'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditAdminModal;
